import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { HarmonizeRunStats } from "encamp-shared/src/violations/harmonizeRunStats";

type HarmonizeRunSummarySectionProps =
  | { stats: HarmonizeRunStats; noTenantsNote?: never }
  | { noTenantsNote: string; stats?: never };

/**
 * Renders the Harmonize Violations run stats stored on `Job.output` for
 * `HARMONIZE_VIOLATIONS` jobs. Mirrors the structure of `WadmRunSummarySection`.
 *
 * Accepts either a full `HarmonizeRunStats` object (normal run) or a
 * `noTenantsNote` string (early-return path when no tenants are flag-enabled).
 */
export function HarmonizeRunSummarySection({
  stats,
  noTenantsNote,
}: HarmonizeRunSummarySectionProps) {
  if (noTenantsNote !== undefined) {
    return (
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Harmonize Violations Run Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary">
            {noTenantsNote}
          </Typography>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Stack spacing={2}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Harmonize Violations Run Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RunSummaryBody stats={stats} />
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Source Counts (Guardrails)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SourceCountsTable sourceCounts={stats.sourceCounts} />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

function RunSummaryBody({ stats }: { stats: HarmonizeRunStats }) {
  const { programCounts, netNewCount, statusFlipCount, staleCount, facilityMatchCount, airtable } =
    stats;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={4} flexWrap="wrap">
        <CounterStat label="Facility matches" value={facilityMatchCount} />
        <CounterStat label="Net-new violations" value={netNewCount} />
        <CounterStat label="Status flips" value={statusFlipCount} />
        <CounterStat label="Stale (not seen this run)" value={staleCount} />
      </Stack>

      <ProgramCountsTable programCounts={programCounts} />

      <AirtableStatsSection airtable={airtable} />
    </Stack>
  );
}

function ProgramCountsTable({ programCounts }: { programCounts: Record<string, number> }) {
  const entries = Object.entries(programCounts);
  if (entries.length === 0) return null;
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        Program counts
      </Typography>
      <TableContainer sx={{ mt: 0.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Program</TableCell>
              <TableCell align="right">Violations</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([program, count]) => (
              <TableRow key={program}>
                <TableCell>{program}</TableCell>
                <TableCell align="right">{count.toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function AirtableStatsSection({ airtable }: { airtable: HarmonizeRunStats["airtable"] }) {
  if ("error" in airtable) {
    return (
      <Box>
        <Typography variant="overline" color="error">
          Airtable sync failed
        </Typography>
        <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
          {airtable.error}
        </Typography>
      </Box>
    );
  }

  const { uploaded, skippedExisting, facilitiesCreated, duplicateIdsInBatch } = airtable;

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        Airtable sync
      </Typography>
      <Stack direction="row" spacing={4} flexWrap="wrap" sx={{ mt: 0.5 }}>
        <CounterStat label="Uploaded" value={uploaded} />
        <CounterStat label="Skipped (existing)" value={skippedExisting} />
        <CounterStat label="Facilities created" value={facilitiesCreated.length} />
        <CounterStat label="Duplicate IDs in batch" value={duplicateIdsInBatch.length} />
      </Stack>
      {duplicateIdsInBatch.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Duplicate IDs ({duplicateIdsInBatch.length})
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {duplicateIdsInBatch.map((id) => (
              <StatusChip key={id} label={id} color="warning" />
            ))}
          </Box>
        </Box>
      )}
      {facilitiesCreated.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="overline" color="text.secondary">
            New Airtable facilities ({facilitiesCreated.length})
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {facilitiesCreated.map((name) => (
              <StatusChip key={name} label={name} color="success" />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function SourceCountsTable({ sourceCounts }: { sourceCounts: Record<string, number> }) {
  const entries = Object.entries(sourceCounts);
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No source counts recorded.
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Source Table</TableCell>
            <TableCell align="right">Row Count</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([source, count]) => (
            <TableRow key={source}>
              <TableCell>{source}</TableCell>
              <TableCell align="right">{count.toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CounterStat({
  label,
  value,
  secondary,
}: {
  label: string;
  value: number;
  secondary?: string;
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight="medium">
        {value.toLocaleString()}
      </Typography>
      {secondary !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {secondary}
        </Typography>
      )}
    </Box>
  );
}
